import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import usePlayerData from "../../hooks/usePlayerData";
import PlayerInfo from "../PlayerPages/PlayerInfo/PlayerInfo";
import PlayerStats2 from "./PlayerStats2";
import "../../styles/theme.css";
import "../PlayerPages/PlayerPage.css";
import { Suspense, lazy, useContext, useEffect, useState } from "react";
import { Team } from "../../models/Team";
import TabsComponent, { PLAYER_TAB_IDS } from "../PlayerPages/PlayerTabs";
import PlayerMatch from "../PlayerPages/PlayerMatch";
import Loading from "../../components/Loading";
import { fetchPlayerBySteamIdAndSeason } from "../../services/PlayerStatService";
import { getLatestSeason } from "../../services/MatchService";
import { AuthContext } from "../../context/AuthContext";
import { fetchCurrentTeam } from "../../services/AccountService";
import { admins, currentSeason } from "../../Constants";
import { Edit } from "@mui/icons-material";

import PlayerAchivementsDisplay from "../PlayerPages/PlayerAchievement/PlayerAchivements";
import useHasFantasyTeam from "../../hooks/useHasFantasyTeam";
import useHasPrediction from "../../hooks/useHasPrediction";
import SiteBanner from "../../components/SiteBanner/SiteBanner";
import WrappedProfileBanner from "../../components/WrappedProfileBanner";
import CompareButton from "../PlayerPages/CompareButton";
import { useDarkMode } from "../../hooks/useDarkmode";
import { getPlayerMapRoles } from "../../services/PlayerRoleService";
import { RoleSummaryDto } from "../../models/PlayerRole";
import styles from "./PlayerPage2.module.css";

const PlayerTeams = lazy(() => import("../PlayerPages/PlayerTeams"));
const PlayerHighlights = lazy(() => import("../PlayerPages/PlayerHighlights/PlayerHighlights"));
const PlayerPositioner = lazy(() => import("../PlayerPages/PlayerPositioner"));

// The player page, served at /player/:steamId. Replaced the original at the season 33
// launch; /player2 redirects here. The action row, season/side selectors, rating tiles and
// "Seneste kampe" table are its own — everything else reuses the shared PlayerPages components.
const PlayerPage2 = () => {
	const { steamId } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [team, setTeam] = useState<Team | null>();
	const [mapRoles, setMapRoles] = useState<RoleSummaryDto[]>([]);
	const { playerData, loading: playerDataLoading } = usePlayerData(steamId);
	const { user: visitor } = useContext(AuthContext);
	const isLoading = playerDataLoading;
	const isDark = useDarkMode();

	const hasFantasyTeam = useHasFantasyTeam(playerData?.steamID);
	const hasPredicted = useHasPrediction(playerData?.steamID);

	// Get the active tab from URL params, then sessionStorage, then default to 'stat'
	// Both sources are untrusted: "?tab=stats" links are out in the wild (the id is "stat") and
	// sessionStorage can hold a tab id from an older build. An id that matches no panel renders a
	// blank page, so anything unrecognised falls through to the default.
	const isKnownTab = (id: string | null): id is string => !!id && PLAYER_TAB_IDS.includes(id);

	const getInitialActiveTab = () => {
		const params = new URLSearchParams(location.search);
		const tabParam = params.get("tab");
		if (isKnownTab(tabParam)) return tabParam;

		const storedTab = sessionStorage.getItem(`player_${steamId}_tab`);
		// Don't restore 'clips' from sessionStorage - it has loading issues
		if (isKnownTab(storedTab) && storedTab !== "clips") return storedTab;

		return "stat";
	};

	const [activeTab, setActiveTab] = useState(() => getInitialActiveTab());

	const handleTabChange = (tabId: string) => {
		setActiveTab(tabId);

		// Update URL with the new tab
		const params = new URLSearchParams(location.search);
		params.set("tab", tabId);
		navigate(`${location.pathname}?${params.toString()}`, { replace: true });

		// Store in sessionStorage (but not 'clips' - it has loading issues when restored)
		if (tabId !== "clips") {
			sessionStorage.setItem(`player_${steamId}_tab`, tabId);
		}
	};

	const [hasPlayedThisSeason, setHasPlayedThisSeason] = useState<boolean | null>(null);

	useEffect(() => {
		const fetchTeam = async () => {
			if (!steamId) return;

			try {
				const team = await fetchCurrentTeam(steamId);
				setTeam(team);
			} catch (e) {
				setTeam(null);
			}

			try {
				const season = await getLatestSeason();
				const playerStat = await fetchPlayerBySteamIdAndSeason(steamId, season);
				setHasPlayedThisSeason(!!playerStat);
			} catch {
				setHasPlayedThisSeason(false);
			}
		};
		fetchTeam();
	}, [steamId]);

	useEffect(() => {
		if (!steamId) return;
		getPlayerMapRoles(steamId, currentSeason).then(setMapRoles).catch(() => setMapRoles([]));
	}, [steamId]);

	// Update active tab if URL changes (for back button support). Unknown ids are ignored: an id that
	// matches no panel renders a blank page, and "?tab=stats" links (the id is "stat") are already out
	// in the wild from the match-page return URL.
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tabParam = params.get("tab");
		if (isKnownTab(tabParam) && tabParam !== activeTab) {
			setActiveTab(tabParam);
		}
	}, [location.search, activeTab]);

	const canEdit = visitor?.steamID === steamId || admins.includes(visitor?.steamID ?? "");


	return (
		<div className="container player-page-container">
			<SiteBanner />
			{isLoading ? (
				<div className="loading-enhanced">
					<Loading />
				</div>
			) : (
				<div>
					{/* Season Wrapped banner — TEMPORARY, remove a few weeks after launch */}
					<WrappedProfileBanner steamId={steamId} playerName={playerData?.playerName} />

					{/* Action row: Rediger left, Fantasy/Forudsigelse right (§1) */}
					{(canEdit || hasFantasyTeam || hasPredicted) && (
						<div className={styles.actionRow}>
							{canEdit && (
								<Link
									to={`/player/${steamId}/edit`}
									className={`${styles.actionBtn} ${styles.actionBtnEdit}`}
								>
									<Edit sx={{ fontSize: 15 }} />
									Rediger
								</Link>
							)}
							{(hasFantasyTeam || hasPredicted) && (
								<div className={styles.actionRight}>
									{hasFantasyTeam && (
										<Link
											to={`/fantasy/view/${steamId}/${currentSeason}`}
											className={styles.actionBtn}
										>
											{playerData?.playerName} Fantasy Hold
										</Link>
									)}
									{hasPredicted && (
										<Link
											to={`/standings/predict/${steamId}`}
											className={styles.actionBtn}
										>
											Se Forudsigelse
										</Link>
									)}
								</div>
							)}
						</div>
					)}

					<PlayerInfo playerData={playerData} teamData={team} mapRoles={mapRoles} kingEffects />

					{hasPlayedThisSeason === false && team && (
						<div className="text-center py-2 px-3 mt-2" style={{
							background: 'rgba(255,255,255,0.05)',
							borderRadius: '6px',
							color: 'rgba(255,255,255,0.5)',
							fontSize: '0.85rem',
							fontStyle: 'italic'
						}}>
							Denne spiller har ikke spillet kampe denne sæson endnu
						</div>
					)}
				</div>
			)}

			<div className="tabs-container">
				<TabsComponent onTabChange={handleTabChange} activeTab={activeTab} />
			</div>
			<hr />
			<div className="content-section">
				{activeTab === "stat" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerStats2 />
					</Suspense>
				)}
				{activeTab === "team" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerTeams />
					</Suspense>
				)}
				{activeTab === "matches" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerMatch team={team} />
					</Suspense>
				)}
				{activeTab === "achievements" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerAchivementsDisplay />
					</Suspense>
				)}
				{activeTab === "clips" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerHighlights />
					</Suspense>
				)}
				{activeTab === "positioner" && (
					<Suspense fallback={<div className="loading-enhanced"><Loading /></div>}>
						<PlayerPositioner />
					</Suspense>
				)}
			</div>

			{/* Add CompareButton component */}
			{!isLoading && steamId && (
				<div className="compare-button-enhanced">
					<CompareButton playerSteamId={steamId} isDark={isDark} />
				</div>
			)}
		</div>
	);
};

export default PlayerPage2;
